// Apex Business Intelligence — Round 3 (additive)
// Business Credentials tab, Insurance tab, Legacy Plan page,
// Insurance Status widget, Legacy Plan Status widget.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "./intelligence";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "apex_auth_token";
const auth = () => { const t = localStorage.getItem(TOKEN_KEY); return t ? {Authorization: "Bearer " + t} : {}; };
const j = (path, opts = {}) =>
  fetch(`${API}${path}`, {...opts, headers: {"Content-Type": "application/json", ...auth(), ...(opts.headers || {})}})
    .then(async r => { const data = await r.json().catch(() => ({})); if (!r.ok) throw Object.assign(new Error(data.detail || r.statusText), {data, status: r.status}); return data; });

const labelStyle = {fontSize:10,letterSpacing:1.2,textTransform:"uppercase",color:"var(--text3)",marginBottom:4,display:"block"};
const inputStyle = {padding:"8px 10px",background:"var(--bg)",border:"1px solid var(--border)",borderRadius:4,color:"var(--text)",fontSize:12,fontFamily:"var(--dm)",width:"100%"};
const card = {background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:8,padding:18,marginBottom:14};
const subhead = {fontFamily:"var(--rajdhani)",fontSize:14,fontWeight:700,marginBottom:10,letterSpacing:0.5};

// ────────────────────────────────────────────────────────────────
// BUSINESS CREDENTIALS TAB (Module 3)
// ────────────────────────────────────────────────────────────────
export function BusinessCredentialsTab() {
  const [doc, setDoc] = useState(null);
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async (rev = reveal) => {
    try { const r = await j(`/api/iv/creds/?reveal=${rev}`); setDoc(r.credentials || {}); }
    catch (e) { toast(e.message || "Load failed", "err"); }
  }, [reveal]);
  useEffect(() => { load(reveal); }, [load, reveal]);

  const setF = (k, v) => setDoc(d => ({...(d||{}), [k]: v}));
  const setBank = (key, k, v) => setDoc(d => ({...(d||{}), [key]: {...(d?.[key]||{}), [k]: v}}));
  const addNaics = () => setDoc(d => ({...(d||{}), naics_codes: [...(d?.naics_codes||[]), ""]}));
  const removeNaics = (i) => setDoc(d => ({...(d||{}), naics_codes: (d?.naics_codes||[]).filter((_,idx) => idx !== i)}));
  const setNaics = (i, v) => setDoc(d => ({...(d||{}), naics_codes: (d?.naics_codes||[]).map((x, idx) => idx === i ? v : x)}));

  const save = async () => {
    setBusy(true);
    try { await j("/api/iv/creds/", {method:"PUT", body: JSON.stringify(doc)}); toast("Saved"); load(reveal); }
    catch (e) { toast(e.message || "Save failed", "err"); }
    finally { setBusy(false); }
  };

  if (!doc) return <div style={{padding:18,color:"var(--text3)"}}>Loading…</div>;
  return (
    <div data-testid="business-creds-tab">
      <div style={{...card,background:"rgba(204,0,0,0.04)",border:"1px solid rgba(204,0,0,0.3)",fontSize:11,color:"var(--text2)"}}>
        🔒 All sensitive fields below are <strong>AES-256 encrypted at rest</strong>. Default view shows last 4 characters only. Toggle "Reveal" to view plaintext — every access is logged in the Audit Trail.
        <label style={{display:"inline-flex",alignItems:"center",gap:8,marginLeft:16,fontSize:11}}>
          <input type="checkbox" data-testid="creds-reveal" checked={reveal} onChange={e => setReveal(e.target.checked)} /> Reveal plaintext
        </label>
      </div>

      <div style={card}>
        <div style={subhead}>Federal Tax Information</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          <div><label style={labelStyle}>EIN (XX-XXXXXXX)</label><input data-testid="creds-ein" style={inputStyle} value={doc.ein||""} onChange={e=>setF("ein",e.target.value)} placeholder="12-3456789"/></div>
          <div><label style={labelStyle}>EIN Legal Name (as filed with IRS)</label><input data-testid="creds-ein-name" style={inputStyle} value={doc.ein_legal_name||""} onChange={e=>setF("ein_legal_name",e.target.value)}/></div>
          <div style={{gridColumn:"1 / -1"}}>
            <label style={{display:"flex",alignItems:"center",gap:8,fontSize:12}}>
              <input type="checkbox" checked={!!doc.ein_filing_address_same_as_business} onChange={e=>setF("ein_filing_address_same_as_business", e.target.checked)}/> EIN filing address is same as business address
            </label>
          </div>
          {!doc.ein_filing_address_same_as_business && <>
            <div><label style={labelStyle}>Street</label><input style={inputStyle} value={doc.ein_filing_street||""} onChange={e=>setF("ein_filing_street",e.target.value)}/></div>
            <div><label style={labelStyle}>City</label><input style={inputStyle} value={doc.ein_filing_city||""} onChange={e=>setF("ein_filing_city",e.target.value)}/></div>
            <div><label style={labelStyle}>State</label><input style={inputStyle} value={doc.ein_filing_state||""} onChange={e=>setF("ein_filing_state",e.target.value)}/></div>
            <div><label style={labelStyle}>ZIP</label><input style={inputStyle} value={doc.ein_filing_zip||""} onChange={e=>setF("ein_filing_zip",e.target.value)}/></div>
          </>}
          <div><label style={labelStyle}>Date EIN Was Issued</label><input type="date" style={inputStyle} value={doc.ein_issued_date||""} onChange={e=>setF("ein_issued_date",e.target.value)}/></div>
        </div>
      </div>

      <div style={card}>
        <div style={subhead}>Dun & Bradstreet</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          <div><label style={labelStyle}>DUNS Number (9 digits, Owner-only)</label><input data-testid="creds-duns" style={inputStyle} value={doc.duns_number||""} onChange={e=>setF("duns_number",e.target.value)}/></div>
          <div><label style={labelStyle}>D&B Credit Rating</label><input style={inputStyle} value={doc.duns_credit_rating||""} onChange={e=>setF("duns_credit_rating",e.target.value)}/></div>
          <div><label style={labelStyle}>D&B PAYDEX Score (1–100)</label><input type="number" min="1" max="100" style={inputStyle} value={doc.duns_paydex||""} onChange={e=>setF("duns_paydex", e.target.value ? Number(e.target.value) : null)}/></div>
          <div><label style={labelStyle}>D&B Profile URL</label><input style={inputStyle} value={doc.duns_profile_url||""} onChange={e=>setF("duns_profile_url",e.target.value)}/></div>
          <div style={{gridColumn:"1 / -1",fontSize:11,color:"var(--text3)"}}>Required for federal contracts, SBA loans, and insurance carrier credentialing. <a href="https://www.dnb.com" target="_blank" rel="noreferrer" style={{color:"#1d4ed8"}}>Apply free at dnb.com →</a></div>
        </div>
      </div>

      <div style={card}>
        <div style={subhead}>State & Entity Registrations</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          <div><label style={labelStyle}>State of Incorporation *</label><input style={inputStyle} value={doc.state_of_incorporation||""} onChange={e=>setF("state_of_incorporation",e.target.value)}/></div>
          <div><label style={labelStyle}>State Registration / Entity Number</label><input style={inputStyle} value={doc.state_registration_number||""} onChange={e=>setF("state_registration_number",e.target.value)}/></div>
          <div><label style={labelStyle}>Date of Formation</label><input type="date" style={inputStyle} value={doc.formation_date||""} onChange={e=>setF("formation_date",e.target.value)}/></div>
          <div><label style={labelStyle}>Registered Agent Name</label><input style={inputStyle} value={doc.registered_agent_name||""} onChange={e=>setF("registered_agent_name",e.target.value)}/></div>
          <div style={{gridColumn:"1 / -1"}}><label style={labelStyle}>Registered Agent Address</label><input style={inputStyle} value={doc.registered_agent_address||""} onChange={e=>setF("registered_agent_address",e.target.value)}/></div>
          <div><label style={labelStyle}>Registered Agent Phone</label><input style={inputStyle} value={doc.registered_agent_phone||""} onChange={e=>setF("registered_agent_phone",e.target.value)}/></div>
          <div><label style={labelStyle}>Registered Agent Email</label><input type="email" style={inputStyle} value={doc.registered_agent_email||""} onChange={e=>setF("registered_agent_email",e.target.value)}/></div>
        </div>
      </div>

      <div style={card}>
        <div style={subhead}>Additional Business IDs</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          <div><label style={labelStyle}>SAM.gov UEI Number</label><input style={inputStyle} value={doc.sam_uei||""} onChange={e=>setF("sam_uei",e.target.value)}/></div>
          <div><label style={labelStyle}>CAGE Code</label><input style={inputStyle} value={doc.cage_code||""} onChange={e=>setF("cage_code",e.target.value)}/></div>
          <div style={{gridColumn:"1 / -1"}}>
            <label style={labelStyle}>SBA Certification</label>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:6}}>
              {["None","8a Business Development","HUBZone","Woman-Owned Small Business (WOSB)","Service-Disabled Veteran-Owned (SDVOSB)","Other"].map(opt => (
                <label key={opt} style={{display:"flex",alignItems:"center",gap:6,fontSize:11}}>
                  <input type="radio" name="sba" checked={doc.sba_certification===opt} onChange={()=>setF("sba_certification", opt)}/> {opt}
                </label>
              ))}
            </div>
            {doc.sba_certification === "Other" && <input style={{...inputStyle, marginTop:8}} placeholder="Specify…" value={doc.sba_certification_other||""} onChange={e=>setF("sba_certification_other",e.target.value)}/>}
          </div>
          <div style={{gridColumn:"1 / -1"}}>
            <label style={labelStyle}>NAICS Codes</label>
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {(doc.naics_codes||[]).map((c, i) => (
                <div key={i} style={{display:"flex",alignItems:"center",gap:4,background:"var(--bg3)",border:"1px solid var(--border)",borderRadius:14,padding:"4px 8px"}}>
                  <input style={{...inputStyle,padding:"2px 4px",width:90,fontSize:11,background:"transparent",border:"none"}} value={c} onChange={e=>setNaics(i, e.target.value)} placeholder="NAICS"/>
                  <button onClick={()=>removeNaics(i)} style={{background:"transparent",border:"none",color:"#cc0000",cursor:"pointer",fontSize:14,lineHeight:1}}>✕</button>
                </div>
              ))}
              <button className="btn btn-ghost" style={{fontSize:11}} onClick={addNaics}>+ Add NAICS</button>
            </div>
          </div>
        </div>
      </div>

      <div style={card}>
        <div style={subhead}>Banking Information <span style={{fontSize:10,color:"#cc0000",letterSpacing:1,marginLeft:8}}>OWNER ONLY</span></div>
        <div style={{fontSize:11,color:"var(--text3)",marginBottom:10}}>These fields are visible only to the Owner — never to any other role under any circumstance.</div>
        <BankAccountRow label="Primary Bank" value={doc.primary_bank||{}} onChange={(k,v)=>setBank("primary_bank",k,v)}/>
        {doc.secondary_bank
          ? <div style={{marginTop:10}}>
              <BankAccountRow label="Secondary Bank" value={doc.secondary_bank||{}} onChange={(k,v)=>setBank("secondary_bank",k,v)}/>
              <button className="btn btn-ghost" style={{fontSize:11,marginTop:6}} onClick={()=>setF("secondary_bank", null)}>Remove secondary bank</button>
            </div>
          : <button className="btn btn-ghost" style={{fontSize:11,marginTop:8}} onClick={()=>setF("secondary_bank", {bank_name:"",routing_number:"",account_number:"",account_type:"Checking"})}>+ Add Secondary Bank Account</button>
        }
      </div>

      <div style={{position:"sticky",bottom:0,background:"var(--bg2)",borderTop:"1px solid var(--border)",padding:"10px 0",textAlign:"right"}}>
        <button data-testid="creds-save" className="btn btn-primary" style={{fontSize:12}} disabled={busy} onClick={save}>{busy ? "Saving…" : "Save Credentials"}</button>
      </div>
    </div>
  );
}

function BankAccountRow({ label, value, onChange }) {
  return (
    <div>
      <div style={{fontFamily:"var(--rajdhani)",fontWeight:600,fontSize:13,marginBottom:6}}>{label}</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <div><label style={labelStyle}>Bank Name</label><input style={inputStyle} value={value.bank_name||""} onChange={e=>onChange("bank_name", e.target.value)}/></div>
        <div><label style={labelStyle}>Account Type</label>
          <select style={inputStyle} value={value.account_type||"Checking"} onChange={e=>onChange("account_type", e.target.value)}>
            {["Checking","Savings","Business Money Market"].map(t => <option key={t}>{t}</option>)}
          </select></div>
        <div><label style={labelStyle}>Routing Number (encrypted)</label><input style={inputStyle} value={value.routing_number||""} onChange={e=>onChange("routing_number", e.target.value)}/></div>
        <div><label style={labelStyle}>Account Number (encrypted)</label><input style={inputStyle} value={value.account_number||""} onChange={e=>onChange("account_number", e.target.value)}/></div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// INSURANCE TAB (Module 4)
// ────────────────────────────────────────────────────────────────
const POLICY_TYPES = ["General Liability", "Workers Compensation", "Commercial Auto", "Umbrella / Excess Liability", "Other"];
const COVERAGE_TYPES = ["General Liability","Completed Operations","Products Liability","Personal and Advertising Injury","Medical Payments"];

export function InsuranceTab() {
  const [policies, setPolicies] = useState([]);
  const [editing, setEditing] = useState(null);
  const refresh = useCallback(() => { j("/api/iv/insurance/policies").then(d => setPolicies(d.policies || [])).catch(() => {}); }, []);
  useEffect(refresh, [refresh]);
  return (
    <div data-testid="insurance-tab">
      <div style={{...card,background:"rgba(29,78,216,0.04)",border:"1px solid rgba(29,78,216,0.3)",fontSize:11,color:"var(--text2)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>📅 <strong>Auto-alerts active</strong> — every policy with an expiration date automatically schedules reminders at 90 / 60 / 30 / 7 days before. Alerts arrive in the dashboard bell + email to Owner.</div>
        <button className="btn btn-primary" data-testid="ins-add" style={{fontSize:12}} onClick={() => setEditing({policy_type: "General Liability", carrier:"", policy_number:"", coverage_types:[], states_covered:[], exempt_officers:[], additional_insureds:[]})}>+ Add Policy</button>
      </div>

      {policies.length === 0
        ? <div style={{...card,color:"var(--text3)",fontSize:12,textAlign:"center"}}>No insurance policies recorded yet. Click + Add Policy to get started.</div>
        : <table style={{width:"100%"}}><thead><tr><th>Type</th><th>Carrier</th><th>Policy #</th><th>Effective</th><th>Expires</th><th></th></tr></thead>
            <tbody>{policies.map(p => (
              <tr key={p.id}>
                <td>{p.policy_type}</td>
                <td>{p.carrier}</td>
                <td style={{fontFamily:"var(--mono)"}}>{p.policy_number}</td>
                <td>{p.policy_effective_date}</td>
                <td>{p.policy_expiration_date}</td>
                <td style={{textAlign:"right"}}>
                  <button className="btn btn-ghost" style={{fontSize:10,marginRight:4}} onClick={() => setEditing(p)}>Edit</button>
                  <button className="btn btn-danger" style={{fontSize:10}} onClick={async () => { if (!window.confirm("Delete policy?")) return; await j(`/api/iv/insurance/policies/${p.id}`, {method:"DELETE"}); toast("Deleted"); refresh(); }}>Delete</button>
                </td>
              </tr>
            ))}</tbody>
          </table>}

      {editing && <PolicyEditor policy={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }}/>}
    </div>
  );
}

function PolicyEditor({ policy, onClose, onSaved }) {
  const [p, setP] = useState(policy);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setP(prev => ({...prev, [k]: v}));
  const toggleCov = (c) => setP(prev => ({...prev, coverage_types: (prev.coverage_types||[]).includes(c) ? prev.coverage_types.filter(x=>x!==c) : [...(prev.coverage_types||[]), c]}));
  const submit = async () => {
    setBusy(true);
    try {
      if (p.id) { await j(`/api/iv/insurance/policies/${p.id}`, {method:"PUT", body: JSON.stringify(p)}); }
      else { await j("/api/iv/insurance/policies", {method:"POST", body: JSON.stringify(p)}); }
      toast("Saved — alerts auto-scheduled for 90/60/30/7 days"); onSaved();
    } catch (e) { toast(e.message || "Save failed", "err"); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-overlay" data-testid="policy-editor">
      <div className="modal" style={{maxWidth:760}}>
        <div className="modal-header">
          <div style={{fontFamily:"var(--rajdhani)",fontWeight:700,fontSize:16}}>{p.id ? "Edit Policy" : "Add Policy"}</div>
          <button className="icon-btn" onClick={onClose}>✕</button>
        </div>
        <div style={{padding:18,maxHeight:"70vh",overflowY:"auto"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div><label style={labelStyle}>Policy Type *</label>
              <select style={inputStyle} value={p.policy_type} onChange={e=>set("policy_type", e.target.value)}>
                {POLICY_TYPES.map(t => <option key={t}>{t}</option>)}
              </select></div>
            <div><label style={labelStyle}>Carrier *</label><input style={inputStyle} value={p.carrier||""} onChange={e=>set("carrier", e.target.value)}/></div>
            {p.policy_type === "Other" && <div style={{gridColumn:"1 / -1"}}><label style={labelStyle}>Policy Type (other)</label><input style={inputStyle} value={p.policy_type_other||""} onChange={e=>set("policy_type_other", e.target.value)} placeholder="e.g. E&O, Cyber Liability, Inland Marine"/></div>}
            <div><label style={labelStyle}>Agent Name</label><input style={inputStyle} value={p.agent_name||""} onChange={e=>set("agent_name", e.target.value)}/></div>
            <div><label style={labelStyle}>Agent Phone</label><input style={inputStyle} value={p.agent_phone||""} onChange={e=>set("agent_phone", e.target.value)}/></div>
            <div><label style={labelStyle}>Agent Email</label><input style={inputStyle} value={p.agent_email||""} onChange={e=>set("agent_email", e.target.value)}/></div>
            <div><label style={labelStyle}>Agency Name</label><input style={inputStyle} value={p.agency_name||""} onChange={e=>set("agency_name", e.target.value)}/></div>
            <div><label style={labelStyle}>Policy Number (encrypted)</label><input style={inputStyle} value={p.policy_number||""} onChange={e=>set("policy_number", e.target.value)}/></div>
            <div><label style={labelStyle}>Policy Kind</label>
              <select style={inputStyle} value={p.policy_kind||"Occurrence"} onChange={e=>set("policy_kind", e.target.value)}>
                <option>Occurrence</option><option>Claims-Made</option>
              </select></div>
            <div><label style={labelStyle}>Effective Date *</label><input type="date" style={inputStyle} value={p.policy_effective_date||""} onChange={e=>set("policy_effective_date", e.target.value)}/></div>
            <div><label style={labelStyle}>Expiration Date *</label><input type="date" style={inputStyle} value={p.policy_expiration_date||""} onChange={e=>set("policy_expiration_date", e.target.value)}/></div>
            <div><label style={labelStyle}>Annual Premium (Owner-only)</label><input type="number" style={inputStyle} value={p.annual_premium||0} onChange={e=>set("annual_premium", Number(e.target.value)||0)}/></div>
            <div><label style={labelStyle}>Deductible</label><input type="number" style={inputStyle} value={p.deductible||0} onChange={e=>set("deductible", Number(e.target.value)||0)}/></div>

            {/* GL fields */}
            {p.policy_type === "General Liability" && <>
              <div style={{gridColumn:"1 / -1"}}>
                <label style={labelStyle}>Coverage Types</label>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:6}}>
                  {COVERAGE_TYPES.map(c => (
                    <label key={c} style={{display:"flex",alignItems:"center",gap:6,fontSize:11}}>
                      <input type="checkbox" checked={(p.coverage_types||[]).includes(c)} onChange={()=>toggleCov(c)}/> {c}
                    </label>
                  ))}
                </div>
              </div>
              <div><label style={labelStyle}>Per Occurrence Limit</label><input type="number" style={inputStyle} value={p.per_occurrence_limit||0} onChange={e=>set("per_occurrence_limit", Number(e.target.value)||0)}/></div>
              <div><label style={labelStyle}>General Aggregate</label><input type="number" style={inputStyle} value={p.general_aggregate_limit||0} onChange={e=>set("general_aggregate_limit", Number(e.target.value)||0)}/></div>
              <div><label style={labelStyle}>Products / Completed Ops</label><input type="number" style={inputStyle} value={p.products_completed_ops||0} onChange={e=>set("products_completed_ops", Number(e.target.value)||0)}/></div>
              <div><label style={labelStyle}>Personal & Adv Limit</label><input type="number" style={inputStyle} value={p.personal_advertising_limit||0} onChange={e=>set("personal_advertising_limit", Number(e.target.value)||0)}/></div>
              <div><label style={labelStyle}>Medical Expense Limit</label><input type="number" style={inputStyle} value={p.medical_expense_limit||0} onChange={e=>set("medical_expense_limit", Number(e.target.value)||0)}/></div>
            </>}

            {/* WC fields */}
            {p.policy_type === "Workers Compensation" && <>
              <div><label style={labelStyle}>NCCI Code</label><input style={inputStyle} value={p.carrier_ncci_code||""} onChange={e=>set("carrier_ncci_code", e.target.value)}/></div>
              <div><label style={labelStyle}>EMR (e.g. 0.85)</label><input type="number" step="0.01" style={inputStyle} value={p.emr||""} onChange={e=>set("emr", e.target.value ? Number(e.target.value) : null)}/></div>
              <div><label style={labelStyle}>Employer Liab — Each Accident</label><input type="number" style={inputStyle} value={p.employer_liability_each_accident||0} onChange={e=>set("employer_liability_each_accident", Number(e.target.value)||0)}/></div>
              <div><label style={labelStyle}>Disease Policy Limit</label><input type="number" style={inputStyle} value={p.employer_liability_disease_policy||0} onChange={e=>set("employer_liability_disease_policy", Number(e.target.value)||0)}/></div>
              <div><label style={labelStyle}>Disease Each Employee</label><input type="number" style={inputStyle} value={p.employer_liability_disease_each||0} onChange={e=>set("employer_liability_disease_each", Number(e.target.value)||0)}/></div>
              <div><label style={labelStyle}>Annual Payroll (Owner-only)</label><input type="number" style={inputStyle} value={p.annual_payroll||0} onChange={e=>set("annual_payroll", Number(e.target.value)||0)}/></div>
              <div style={{gridColumn:"1 / -1"}}>
                <label style={labelStyle}>Open Claims</label>
                <div style={{display:"flex",gap:14,fontSize:12}}>
                  <label><input type="radio" name="oc" checked={p.open_claims==="No"} onChange={()=>set("open_claims","No")}/> No open/pending claims</label>
                  <label><input type="radio" name="oc" checked={p.open_claims==="Yes"} onChange={()=>set("open_claims","Yes")}/> Yes</label>
                </div>
                {p.open_claims === "Yes" && <textarea style={{...inputStyle,marginTop:6,minHeight:60}} value={p.open_claims_description||""} onChange={e=>set("open_claims_description", e.target.value)} placeholder="Describe open claims…"/>}
              </div>
            </>}

            {/* Auto fields */}
            {p.policy_type === "Commercial Auto" && <>
              <div><label style={labelStyle}>Combined Single Limit</label><input type="number" style={inputStyle} value={p.combined_single_limit||0} onChange={e=>set("combined_single_limit", Number(e.target.value)||0)}/></div>
              <div><label style={labelStyle}>Vehicles Covered</label>
                <select style={inputStyle} value={p.vehicles_covered||""} onChange={e=>set("vehicles_covered", e.target.value)}>
                  <option value="">— select —</option>
                  {["Any Auto","All Owned Autos","Scheduled Autos","Hired Autos","Non-Owned Autos"].map(x => <option key={x}>{x}</option>)}
                </select></div>
            </>}

            {/* Umbrella fields */}
            {p.policy_type === "Umbrella / Excess Liability" && <>
              <div><label style={labelStyle}>Umbrella Limit</label><input type="number" style={inputStyle} value={p.umbrella_limit||0} onChange={e=>set("umbrella_limit", Number(e.target.value)||0)}/></div>
              <div><label style={labelStyle}>Self-Insured Retention</label><input type="number" style={inputStyle} value={p.self_insured_retention||0} onChange={e=>set("self_insured_retention", Number(e.target.value)||0)}/></div>
            </>}

            <div style={{gridColumn:"1 / -1"}}>
              <label style={{display:"flex",alignItems:"center",gap:8,fontSize:12}}><input type="checkbox" checked={!!p.coi_on_file} onChange={e=>set("coi_on_file", e.target.checked)}/> COI on file</label>
              {p.coi_on_file && <>
                <input type="file" style={{marginTop:6}} onChange={e => {
                  const f = e.target.files?.[0]; if (!f) return;
                  if (f.size > 10*1024*1024) return toast("Max 10MB", "err");
                  const r = new FileReader(); r.onload = () => set("coi", {filename: f.name, data_b64: r.result, uploaded_at: new Date().toISOString()}); r.readAsDataURL(f);
                }}/>
                {p.coi?.filename && <div style={{fontSize:11,color:"var(--text3)",marginTop:4}}>Uploaded: {p.coi.filename}</div>}
              </>}
            </div>

            <div style={{gridColumn:"1 / -1"}}><label style={labelStyle}>Notes / Special Conditions</label><textarea style={{...inputStyle, minHeight:60}} value={p.notes||""} onChange={e=>set("notes", e.target.value)}/></div>
          </div>
        </div>
        <div style={{padding:14,borderTop:"1px solid var(--border)",background:"var(--bg3)",display:"flex",justifyContent:"flex-end",gap:8}}>
          <button className="btn btn-ghost" onClick={onClose} style={{fontSize:11}}>Cancel</button>
          <button className="btn btn-primary" disabled={busy} onClick={submit} data-testid="policy-save" style={{fontSize:11}}>{busy ? "Saving…" : "Save Policy"}</button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// INSURANCE STATUS WIDGET (dashboard, Owner-only)
// ────────────────────────────────────────────────────────────────
export function InsuranceStatusWidget({ onManage }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { j("/api/iv/insurance/status").then(d => setRows(d.policies || [])).catch(() => {}); }, []);
  if (rows.length === 0) return null;
  const urgent = rows.filter(r => ["red","yellow"].includes(r.light));
  return (
    <div data-testid="insurance-status-widget" style={{...card,marginBottom:16,borderColor:urgent.find(r=>r.light==="red")?"#cc0000":"var(--border)"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <div style={{fontFamily:"var(--rajdhani)",fontSize:15,fontWeight:700}}>Insurance Status</div>
        <button className="btn btn-ghost" style={{fontSize:11}} onClick={onManage} data-testid="ins-manage">Manage Insurance →</button>
      </div>
      <div style={{display:"grid",gap:6}}>
        {rows.map(r => (
          <div key={r.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 10px",background:"var(--bg3)",borderRadius:4,fontSize:12}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{width:10,height:10,borderRadius:"50%",background: r.light==="green"?"#1d4ed8":r.light==="yellow"?"#f59e0b":r.light==="red"?"#cc0000":"#888"}}/>
              {r.policy_type} <span style={{color:"var(--text3)"}}>· {r.carrier}</span>
            </div>
            <div style={{color:r.light==="red"?"#cc0000":"var(--text2)"}}>
              {r.days_remaining != null ? (r.days_remaining < 0 ? `Expired ${-r.days_remaining}d ago` : `${r.days_remaining}d left · ${r.expiration_date}`) : "No expiration set"}
            </div>
          </div>
        ))}
      </div>
      {urgent.find(r => r.light === "red") && <div style={{marginTop:8,fontSize:11,color:"#cc0000"}}>⚠ One or more policies expire within 30 days.</div>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// LEGACY PLAN PAGE (Module 5)
// ────────────────────────────────────────────────────────────────
const RELATIONSHIPS = ["Spouse or Partner","Adult Child","Attorney","Business Partner","Accountant or CPA","Trusted Employee","Other"];
const ACCESS_LEVELS = ["Full account ownership transfer","Temporary account management only","View-only financial records","View-only all business data","Ability to add and remove team members","Ability to cancel or transfer subscription","Access to all stored documents","Access to insurance policy information","Access to banking information","Access to credentials vault"];

export function LegacyPlanPage() {
  const [plan, setPlan] = useState(null);
  const [vault, setVault] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await j("/api/iv/legacy/plan"); setPlan(r.plan || {});
          const v = await j("/api/iv/legacy/vault"); setVault(v.entries || []);
          const t = await j("/api/iv/legacy/triggers"); setTriggers(t.triggers || []);
    } catch (e) { toast(e.message || "Load failed", "err"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setPlan(p => ({...(p||{}), [k]: v}));
  const setNested = (group, k, v) => setPlan(p => ({...(p||{}), [group]: {...(p?.[group]||{}), [k]: v}}));

  const save = async () => {
    setBusy(true);
    try { await j("/api/iv/legacy/plan", {method:"PUT", body: JSON.stringify(plan)}); toast("Saved — confirmation emails sent"); load(); }
    catch (e) { toast(e.message || "Save failed", "err"); }
    finally { setBusy(false); }
  };

  if (!plan) return <div style={{padding:18,color:"var(--text3)"}}>Loading…</div>;
  const pc = plan.primary_contact || {};
  const sc = plan.secondary_contact || {};
  const toggleAccess = (lvl) => setNested("primary_contact", "access_levels", (pc.access_levels||[]).includes(lvl) ? pc.access_levels.filter(x=>x!==lvl) : [...(pc.access_levels||[]), lvl]);

  return (
    <div data-testid="legacy-plan-page">
      <div style={{...card,background:"rgba(204,0,0,0.04)",border:"1px solid rgba(204,0,0,0.3)",fontSize:11,color:"var(--text2)"}}>
        ⚖️ <strong>Disclaimer.</strong> This tool helps you organize and share account access information in case of emergency. It is not a substitute for a formal will, trust, power of attorney, or legal succession plan. Please consult a licensed attorney for legal succession planning.
      </div>

      <LegacyStatusWidget compact />

      <div style={card}>
        <div style={subhead}>Primary Legacy Contact</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <div><label style={labelStyle}>Full Name *</label><input data-testid="lg-primary-name" style={inputStyle} value={pc.full_name||""} onChange={e=>setNested("primary_contact","full_name",e.target.value)}/></div>
          <div><label style={labelStyle}>Relationship</label>
            <select style={inputStyle} value={pc.relationship||""} onChange={e=>setNested("primary_contact","relationship",e.target.value)}>
              <option value="">— select —</option>{RELATIONSHIPS.map(r => <option key={r}>{r}</option>)}
            </select></div>
          {pc.relationship === "Other" && <div style={{gridColumn:"1 / -1"}}><input style={inputStyle} placeholder="Specify relationship" value={pc.relationship_other||""} onChange={e=>setNested("primary_contact","relationship_other",e.target.value)}/></div>}
          <div><label style={labelStyle}>Email *</label><input type="email" style={inputStyle} value={pc.email||""} onChange={e=>setNested("primary_contact","email",e.target.value)}/></div>
          <div><label style={labelStyle}>Primary Phone *</label><input style={inputStyle} value={pc.primary_phone||""} onChange={e=>setNested("primary_contact","primary_phone",e.target.value)}/></div>
          <div><label style={labelStyle}>Phone Type</label>
            <select style={inputStyle} value={pc.primary_phone_type||"Mobile"} onChange={e=>setNested("primary_contact","primary_phone_type",e.target.value)}>
              {["Mobile","Home","Work"].map(t => <option key={t}>{t}</option>)}
            </select></div>
          <div><label style={labelStyle}>Secondary Phone</label><input style={inputStyle} value={pc.secondary_phone||""} onChange={e=>setNested("primary_contact","secondary_phone",e.target.value)}/></div>
          <div style={{gridColumn:"1 / -1"}}><label style={labelStyle}>Mailing Address</label>
            <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:8}}>
              <input style={inputStyle} placeholder="Street" value={pc.mailing_street||""} onChange={e=>setNested("primary_contact","mailing_street",e.target.value)}/>
              <input style={inputStyle} placeholder="City" value={pc.mailing_city||""} onChange={e=>setNested("primary_contact","mailing_city",e.target.value)}/>
              <input style={inputStyle} placeholder="State" value={pc.mailing_state||""} onChange={e=>setNested("primary_contact","mailing_state",e.target.value)}/>
              <input style={inputStyle} placeholder="ZIP" value={pc.mailing_zip||""} onChange={e=>setNested("primary_contact","mailing_zip",e.target.value)}/>
            </div>
          </div>
          <div><label style={labelStyle}>Date of Birth (encrypted)</label><input type="date" style={inputStyle} value={pc.date_of_birth||""} onChange={e=>setNested("primary_contact","date_of_birth",e.target.value)}/></div>
        </div>

        <div style={{marginTop:12}}>
          <label style={labelStyle}>Access Level</label>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:4}}>
            {ACCESS_LEVELS.map(lvl => (
              <label key={lvl} style={{display:"flex",alignItems:"center",gap:8,fontSize:11}}>
                <input type="checkbox" checked={(pc.access_levels||[]).includes(lvl)} onChange={()=>toggleAccess(lvl)}/> {lvl}
              </label>
            ))}
          </div>
        </div>

        <div style={{marginTop:12}}>
          <label style={labelStyle}>Personal Message (encrypted, only shown when access granted)</label>
          <textarea maxLength={2000} style={{...inputStyle,minHeight:80}} value={pc.personal_message||""} onChange={e=>setNested("primary_contact","personal_message",e.target.value)}/>
          <div style={{fontSize:10,color:"var(--text3)",textAlign:"right"}}>{(pc.personal_message||"").length}/2000</div>
        </div>
      </div>

      <div style={card}>
        <div style={subhead}>Secondary Legacy Contact</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <div><label style={labelStyle}>Full Name</label><input style={inputStyle} value={sc.full_name||""} onChange={e=>setNested("secondary_contact","full_name",e.target.value)}/></div>
          <div><label style={labelStyle}>Relationship</label>
            <select style={inputStyle} value={sc.relationship||""} onChange={e=>setNested("secondary_contact","relationship",e.target.value)}>
              <option value="">— select —</option>{RELATIONSHIPS.map(r => <option key={r}>{r}</option>)}
            </select></div>
          <div><label style={labelStyle}>Email</label><input type="email" style={inputStyle} value={sc.email||""} onChange={e=>setNested("secondary_contact","email",e.target.value)}/></div>
          <div><label style={labelStyle}>Primary Phone</label><input style={inputStyle} value={sc.primary_phone||""} onChange={e=>setNested("secondary_contact","primary_phone",e.target.value)}/></div>
          <div><label style={labelStyle}>Notify after N days if primary unreachable</label>
            <select style={inputStyle} value={sc.notify_after_days||7} onChange={e=>setNested("secondary_contact","notify_after_days",Number(e.target.value))}>
              {[3,5,7,14].map(d => <option key={d} value={d}>{d} days</option>)}
            </select></div>
        </div>
      </div>

      <div style={card}>
        <div style={subhead}>Legal & Professional Advisors</div>
        {[["attorney","Attorney"],["accountant","Accountant / CPA"],["financial_advisor","Financial Advisor"],["insurance_broker","Insurance Broker"]].map(([key,label]) => {
          const a = plan[key] || {};
          return (
            <div key={key} style={{marginBottom:14}}>
              <div style={{fontFamily:"var(--rajdhani)",fontWeight:600,fontSize:13,marginBottom:6}}>{label}</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8}}>
                <input style={inputStyle} placeholder="Firm" value={a.firm||""} onChange={e=>setNested(key,"firm",e.target.value)}/>
                <input style={inputStyle} placeholder="Name" value={a.name||""} onChange={e=>setNested(key,"name",e.target.value)}/>
                <input style={inputStyle} placeholder="Phone" value={a.phone||""} onChange={e=>setNested(key,"phone",e.target.value)}/>
                <input style={inputStyle} placeholder="Email" value={a.email||""} onChange={e=>setNested(key,"email",e.target.value)}/>
              </div>
              {key === "attorney" && (
                <div style={{marginTop:6,fontSize:11,display:"flex",gap:14}}>
                  <span style={{color:"var(--text3)"}}>Briefed:</span>
                  {["Yes","No","In Progress"].map(s => (
                    <label key={s} style={{display:"flex",alignItems:"center",gap:4}}>
                      <input type="radio" name={`brief-${key}`} checked={(a.briefed||"No")===s} onChange={()=>setNested(key,"briefed",s)}/> {s}
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={card}>
        <div style={subhead}>Critical Documents</div>
        {[["last_will","Last Will and Testament"],["succession_plan","Business Succession Plan"],["operating_agreement","Operating / Partnership Agreement"]].map(([key,label]) => (
          <DocBlock key={key} label={label} value={plan[key]||{}} onChange={(k,v)=>setNested(key,k,v)} hasUpload={(plan[key]||{}).has_upload}/>
        ))}
        <div style={{fontFamily:"var(--rajdhani)",fontWeight:600,fontSize:13,marginTop:10,marginBottom:6}}>Safe / Lockbox <span style={{fontSize:10,color:"#cc0000",letterSpacing:1}}>OWNER ONLY</span></div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <input style={inputStyle} placeholder="Physical location" value={(plan.safe_lockbox||{}).physical_location||""} onChange={e=>setNested("safe_lockbox","physical_location",e.target.value)}/>
          <input style={inputStyle} placeholder="Combination or key location" value={(plan.safe_lockbox||{}).combination_or_key_location||""} onChange={e=>setNested("safe_lockbox","combination_or_key_location",e.target.value)}/>
        </div>
        <div style={{fontFamily:"var(--rajdhani)",fontWeight:600,fontSize:13,marginTop:10,marginBottom:6}}>Safe Deposit Box</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr",gap:8}}>
          <input style={inputStyle} placeholder="Bank" value={(plan.safe_deposit_box||{}).bank||""} onChange={e=>setNested("safe_deposit_box","bank",e.target.value)}/>
          <input style={inputStyle} placeholder="Branch" value={(plan.safe_deposit_box||{}).branch||""} onChange={e=>setNested("safe_deposit_box","branch",e.target.value)}/>
          <input style={inputStyle} placeholder="Box #" value={(plan.safe_deposit_box||{}).box_number||""} onChange={e=>setNested("safe_deposit_box","box_number",e.target.value)}/>
          <input style={inputStyle} placeholder="Key location" value={(plan.safe_deposit_box||{}).key_location||""} onChange={e=>setNested("safe_deposit_box","key_location",e.target.value)}/>
          <input style={inputStyle} placeholder="Authorized person" value={(plan.safe_deposit_box||{}).authorized_person||""} onChange={e=>setNested("safe_deposit_box","authorized_person",e.target.value)}/>
        </div>
      </div>

      <CredentialsVault vault={vault} onChanged={load}/>

      <div style={card}>
        <div style={subhead}>Succession Trigger & Verification</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <div><label style={labelStyle}>Activation Method *</label>
            <select style={inputStyle} value={plan.activation_method} onChange={e=>set("activation_method", e.target.value)}>
              {["Legacy contact submits formal written request with identity verification","Legacy contact provides pre-set verification code","Both required — recommended"].map(o => <option key={o}>{o}</option>)}
            </select></div>
          <div><label style={labelStyle}>Waiting Period *</label>
            <select style={inputStyle} value={plan.waiting_period} onChange={e=>set("waiting_period", e.target.value)}>
              {["Immediate","24 hours","72 hours recommended","7 days"].map(o => <option key={o}>{o}</option>)}
            </select></div>
          <div style={{gridColumn:"1 / -1"}}><label style={labelStyle}>Notifications</label>
            <div style={{display:"flex",gap:18,fontSize:12}}>
              <label><input type="checkbox" checked={!!plan.notify_email} onChange={e=>set("notify_email",e.target.checked)}/> Notify Owner email</label>
              <label><input type="checkbox" checked={!!plan.notify_sms} onChange={e=>set("notify_sms",e.target.checked)}/> Notify Owner SMS</label>
              <label><input type="checkbox" checked={!!plan.notify_secondary} onChange={e=>set("notify_secondary",e.target.checked)}/> Notify secondary simultaneously</label>
            </div>
          </div>
          <div><label style={labelStyle}>Annual Review Reminder</label>
            <select style={inputStyle} value={plan.review_cadence} onChange={e=>set("review_cadence", e.target.value)}>
              {["Every 6 months","Every year recommended","Every 2 years"].map(o => <option key={o}>{o}</option>)}
            </select></div>
          <div>
            <label style={labelStyle}>Verification Code</label>
            <VerificationCodeControl />
          </div>
          <div style={{gridColumn:"1 / -1",fontSize:11,color:"var(--text3)"}}>
            Last updated: <strong>{plan.last_updated ? new Date(plan.last_updated).toLocaleString() : "Never"}</strong> · Next review due: <strong>{plan.next_review_due || "—"}</strong>
          </div>
        </div>
      </div>

      {triggers.length > 0 && (
        <div style={card}>
          <div style={subhead}>Succession Trigger Requests</div>
          <table><thead><tr><th>When</th><th>Requested By</th><th>Code Match</th><th>Status</th></tr></thead>
            <tbody>{triggers.map(t => (
              <tr key={t.id}>
                <td style={{fontSize:11,color:"var(--text3)"}}>{new Date(t.created_at).toLocaleString()}</td>
                <td>{t.requested_by_email}</td>
                <td><span className={`badge ${t.code_match ? "badge-green" : "badge-red"}`}>{t.code_match ? "Match" : "No Match"}</span></td>
                <td><span className="badge badge-cyan">{t.status}</span></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      <div style={{position:"sticky",bottom:0,background:"var(--bg2)",borderTop:"1px solid var(--border)",padding:"10px 0",textAlign:"right"}}>
        <button data-testid="legacy-save" className="btn btn-primary" disabled={busy} onClick={save} style={{fontSize:12}}>{busy ? "Saving…" : "Save Legacy Plan"}</button>
      </div>
    </div>
  );
}

function DocBlock({ label, value, onChange, hasUpload }) {
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 10*1024*1024) return toast("Max 10MB", "err");
    const r = new FileReader();
    r.onload = () => { onChange("upload_b64", r.result); onChange("upload_filename", f.name); };
    r.readAsDataURL(f);
  };
  return (
    <div style={{marginBottom:12}}>
      <div style={{fontFamily:"var(--rajdhani)",fontWeight:600,fontSize:13,marginBottom:4}}>{label}</div>
      <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr",gap:8}}>
        <input style={inputStyle} placeholder="Location (drawer, file cabinet, cloud folder, etc.)" value={value.location||""} onChange={e=>onChange("location", e.target.value)}/>
        <input style={inputStyle} placeholder="Attorney" value={value.attorney||""} onChange={e=>onChange("attorney", e.target.value)}/>
        <input type="file" onChange={onFile}/>
      </div>
      {(hasUpload || value.upload_filename) && <div style={{fontSize:11,color:"#1d4ed8",marginTop:4}}>✓ Encrypted document on file{value.upload_filename ? ` — ${value.upload_filename}` : ""}</div>}
    </div>
  );
}

function VerificationCodeControl() {
  const [val, setVal] = useState("");
  const [showSet, setShowSet] = useState(false);
  const set = async () => {
    if (val.length < 6) return toast("At least 6 characters", "err");
    try { await j("/api/iv/legacy/plan/verification-code", {method:"POST", body: JSON.stringify({code: val})}); toast("Verification code set"); setVal(""); setShowSet(false); }
    catch (e) { toast(e.message || "Failed", "err"); }
  };
  const reset = async () => {
    if (!window.confirm("Reset the verification code? Legacy contact will need a new code.")) return;
    try { await j("/api/iv/legacy/plan/verification-code", {method:"DELETE"}); toast("Code reset"); }
    catch (e) { toast(e.message || "Failed", "err"); }
  };
  return (
    <div style={{display:"flex",gap:6,alignItems:"center"}}>
      {!showSet
        ? <>
            <button className="btn btn-ghost" style={{fontSize:11}} onClick={()=>setShowSet(true)} data-testid="vc-set">Set Verification Code</button>
            <button className="btn btn-ghost" style={{fontSize:11}} onClick={reset} data-testid="vc-reset">Reset Code</button>
          </>
        : <>
            <input style={{...inputStyle,maxWidth:160}} type="password" placeholder="6+ chars" value={val} onChange={e=>setVal(e.target.value)}/>
            <button className="btn btn-primary" style={{fontSize:11}} onClick={set} data-testid="vc-save">Save</button>
            <button className="btn btn-ghost" style={{fontSize:11}} onClick={()=>setShowSet(false)}>Cancel</button>
          </>}
    </div>
  );
}

function CredentialsVault({ vault, onChanged }) {
  const [add, setAdd] = useState(null);
  const [revealId, setRevealId] = useState(null);
  const [revealed, setRevealed] = useState(null);
  const onSave = async () => {
    if (!add.platform) return toast("Platform required", "err");
    try {
      if (add.id) await j(`/api/iv/legacy/vault/${add.id}`, {method:"PUT", body: JSON.stringify(add)});
      else await j("/api/iv/legacy/vault", {method:"POST", body: JSON.stringify(add)});
      toast("Saved"); setAdd(null); onChanged();
    } catch (e) { toast(e.message || "Failed", "err"); }
  };
  const onReveal = async (id) => {
    try { const r = await j(`/api/iv/legacy/vault/${id}`); setRevealed(r); setRevealId(id); }
    catch (e) { toast(e.message || "Failed", "err"); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this vault entry?")) return;
    try { await j(`/api/iv/legacy/vault/${id}`, {method:"DELETE"}); toast("Deleted"); onChanged(); }
    catch (e) { toast(e.message || "Failed", "err"); }
  };
  return (
    <div style={card}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={subhead}>Digital Credentials Vault <span style={{fontSize:10,color:"#cc0000",letterSpacing:1,marginLeft:8}}>AES-256</span></div>
        <button className="btn btn-primary" style={{fontSize:11}} onClick={()=>setAdd({platform:"",username:"",password:"",login_url:"",notes:""})} data-testid="vault-add">+ Add Credential</button>
      </div>
      {vault.length === 0
        ? <div style={{fontSize:11,color:"var(--text3)"}}>No credentials stored. Released to legacy contact only when succession is formally triggered.</div>
        : <table><thead><tr><th>Platform</th><th>Username</th><th>Login URL</th><th></th></tr></thead>
          <tbody>{vault.map(v => (
            <tr key={v.id}>
              <td>{v.platform}</td>
              <td style={{fontFamily:"var(--mono)"}}>{v.username_masked}</td>
              <td style={{fontSize:11}}>{v.login_url && <a href={v.login_url} target="_blank" rel="noreferrer" style={{color:"#1d4ed8"}}>{v.login_url}</a>}</td>
              <td style={{textAlign:"right"}}>
                <button className="btn btn-ghost" style={{fontSize:10,marginRight:4}} onClick={()=>onReveal(v.id)}>View</button>
                <button className="btn btn-ghost" style={{fontSize:10,marginRight:4}} onClick={async ()=>{ const full = await j(`/api/iv/legacy/vault/${v.id}`); setAdd({...full, id: v.id}); }}>Edit</button>
                <button className="btn btn-danger" style={{fontSize:10}} onClick={()=>onDelete(v.id)}>Delete</button>
              </td>
            </tr>
          ))}</tbody></table>}

      {add && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:500}}>
            <div className="modal-header">
              <div style={{fontFamily:"var(--rajdhani)",fontWeight:700}}>{add.id ? "Edit Credential" : "Add Credential"}</div>
              <button className="icon-btn" onClick={()=>setAdd(null)}>✕</button>
            </div>
            <div style={{padding:18,display:"grid",gap:10}}>
              <input style={inputStyle} placeholder="Platform / Service" value={add.platform} onChange={e=>setAdd({...add, platform: e.target.value})} data-testid="vault-platform"/>
              <input style={inputStyle} placeholder="Username or Email" value={add.username} onChange={e=>setAdd({...add, username: e.target.value})}/>
              <input style={inputStyle} type="password" placeholder="Password" value={add.password} onChange={e=>setAdd({...add, password: e.target.value})}/>
              <input style={inputStyle} placeholder="Login URL" value={add.login_url} onChange={e=>setAdd({...add, login_url: e.target.value})}/>
              <textarea style={{...inputStyle, minHeight:60}} placeholder="Notes" value={add.notes} onChange={e=>setAdd({...add, notes: e.target.value})}/>
            </div>
            <div style={{padding:14,borderTop:"1px solid var(--border)",background:"var(--bg3)",textAlign:"right"}}>
              <button className="btn btn-ghost" onClick={()=>setAdd(null)} style={{fontSize:11,marginRight:6}}>Cancel</button>
              <button className="btn btn-primary" onClick={onSave} style={{fontSize:11}} data-testid="vault-save">Save Credential</button>
            </div>
          </div>
        </div>
      )}

      {revealId && revealed && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:480}}>
            <div className="modal-header">
              <div style={{fontFamily:"var(--rajdhani)",fontWeight:700}}>{revealed.platform}</div>
              <button className="icon-btn" onClick={()=>{ setRevealId(null); setRevealed(null); }}>✕</button>
            </div>
            <div style={{padding:18,display:"grid",gap:6,fontSize:13}}>
              <div><strong>Username:</strong> <code>{revealed.username}</code></div>
              <div><strong>Password:</strong> <code>{revealed.password}</code></div>
              {revealed.login_url && <div><strong>URL:</strong> <a href={revealed.login_url} target="_blank" rel="noreferrer">{revealed.login_url}</a></div>}
              {revealed.notes && <div><strong>Notes:</strong> {revealed.notes}</div>}
              <div style={{fontSize:10,color:"var(--text3)",marginTop:6}}>This view was logged to the Audit Trail.</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// LEGACY PLAN STATUS WIDGET (Owner-only dashboard widget)
// ────────────────────────────────────────────────────────────────
export function LegacyStatusWidget({ compact, onOpen }) {
  const [s, setS] = useState(null);
  useEffect(() => { j("/api/iv/legacy/status").then(setS).catch(() => {}); }, []);
  if (!s) return null;
  if (!s.configured && compact) return null;
  return (
    <div data-testid="legacy-status-widget" style={{...card,marginBottom:14,background:"var(--bg2)"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <div style={{fontFamily:"var(--rajdhani)",fontSize:15,fontWeight:700}}>Legacy Plan {!s.configured && <span style={{fontSize:11,color:"#cc0000"}}>· not configured</span>}</div>
        {onOpen && <button className="btn btn-ghost" style={{fontSize:11}} onClick={onOpen}>Review & Update →</button>}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:8,fontSize:11}}>
        <Stat label="Primary Contact" value={s.primary_contact || "—"}/>
        <Stat label="Secondary Contact" value={s.secondary_contact || "—"}/>
        <Stat label="Attorney Briefed" value={s.attorney_status}/>
        <Stat label="Vault Credentials" value={s.vault_count}/>
        <Stat label="Documents" value={`${s.documents_complete}/${s.documents_total}`}/>
        <Stat label="Verification Code" value={s.verification_code_set ? "Set" : "Not set"} hot={!s.verification_code_set}/>
        <Stat label="Last Updated" value={s.last_updated ? new Date(s.last_updated).toLocaleDateString() : "—"}/>
        <Stat label="Next Review Due" value={s.next_review_due || "—"}/>
      </div>
    </div>
  );
}

function Stat({label, value, hot}) {
  return (
    <div style={{padding:"6px 10px",background:"var(--bg3)",borderRadius:4}}>
      <div style={{fontSize:9,letterSpacing:1.2,textTransform:"uppercase",color:"var(--text3)"}}>{label}</div>
      <div style={{fontFamily:"var(--rajdhani)",fontSize:14,fontWeight:600,color:hot?"#cc0000":"var(--text)"}}>{value}</div>
    </div>
  );
}
